/**
 * Modal/dialog primitive. Centered card on desktop, full-screen sheet on
 * mobile. Closes on Esc, backdrop click, or close button. Mounted with
 * portal-less rendering for simplicity (Tailwind handles z-index).
 */

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { X } from "lucide-react";
import { useVisualViewport } from "../lib/useVisualViewport";
import { isPrimer } from "../lib/keyboardPrimer";

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea,input,select,[contenteditable="true"],[tabindex]:not([tabindex="-1"])';

// Reserve the larger of the home-indicator safe area or the on-screen keyboard
// (whichever is currently covering the bottom). Never both — the keyboard sits
// over the home indicator — so a single max() avoids double-counting.
const BOTTOM_INSET = "max(env(safe-area-inset-bottom), var(--kb, 0px))";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Header content. Pass a string for a plain title, or a node for custom
   * interactive headers (e.g. inline-editable title + folder picker). */
  title: ReactNode;
  /** Used for aria-label when `title` is a node. Falls back to title if it's
   * already a string. */
  ariaLabel?: string;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
  /** Render edge-to-edge, full-viewport-height (no rounded corners, no
   * backdrop gap). Used for the note editor on installed PWAs. Uses 100dvh so
   * the dialog shrinks when the on-screen keyboard opens. */
  fullScreen?: boolean;
  /** When true, the dialog does NOT grab focus on open — its content focuses
   * itself (e.g. the markdown editor for a new note). Stealing focus to the
   * dialog root here would blur the keyboard-primer input and drop the iOS
   * keyboard before the editor is ready. */
  autoFocusContent?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  ariaLabel,
  children,
  footer,
  maxWidth = "max-w-md",
  fullScreen = false,
  autoFocusContent = false,
}: ModalProps) {
  // Publishes `--kb` (on-screen keyboard height) while open so the dialog can
  // lift its footer above the keyboard via CSS.
  useVisualViewport();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Focus management: move focus into the dialog on open, trap Tab inside it,
  // and restore focus to the trigger on close. (vaul's ActionSheet already does
  // this; this brings NoteEditor/ShareDialog/etc. up to the same bar.)
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const root = dialogRef.current;
    const visibleFocusables = () =>
      root
        ? Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
            (el) => el.offsetParent !== null || el === root
          )
        : [];
    // Initial focus — the dialog itself, unless something inside already has it
    // (e.g. the editor auto-focuses its body for new notes). Skipped entirely
    // when the content focuses itself, so we don't blur the keyboard primer.
    if (!autoFocusContent && root && !root.contains(document.activeElement)) root.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !root) return;
      const f = visibleFocusables();
      if (f.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = f[0];
      const last = f[f.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === root)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    root?.addEventListener("keydown", onKeyDown);
    return () => {
      root?.removeEventListener("keydown", onKeyDown);
      // Restore focus to the opener — but never to the hidden keyboard primer,
      // which would re-open the keyboard after the note closes. Leaving focus on
      // <body> lets iOS dismiss the keyboard as the editor unmounts.
      if (previous && !isPrimer(previous)) previous.focus?.();
    };
  }, [open, autoFocusContent]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? (typeof title === "string" ? title : undefined)}
      className={`fixed inset-0 z-50 flex justify-center ${
        fullScreen ? "items-stretch" : "items-end sm:items-center"
      }`}
      // Pinned to the full viewport (inset-0). For a centered/bottom-sheet
      // dialog, lift it above the keyboard (and the home indicator when no
      // keyboard) by padding the bottom; the full-screen variant does this on
      // its inner panel instead.
      style={fullScreen ? undefined : { paddingBottom: BOTTOM_INSET }}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={
          fullScreen
            ? "relative w-full h-full flex flex-col overflow-hidden outline-none"
            : `relative w-full ${maxWidth} sm:rounded-lg shadow-xl flex flex-col max-h-full overflow-hidden outline-none`
        }
        style={{
          background: "var(--bg-elevated)",
          color: "var(--fg)",
          ...(fullScreen
            ? {
                paddingTop: "env(safe-area-inset-top)",
                paddingBottom: BOTTOM_INSET,
              }
            : {}),
        }}
      >
        <div
          className="flex items-center justify-between px-4 py-3 border-b gap-2"
          style={{ borderColor: "var(--border)" }}
        >
          {typeof title === "string" ? (
            <h2 className="text-sm font-semibold truncate">{title}</h2>
          ) : (
            <div className="text-sm font-semibold min-w-0 flex-1">{title}</div>
          )}
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 py-3 overflow-y-auto flex-1">{children}</div>
        {footer && (
          <div
            className="px-4 py-3 border-t flex justify-end gap-2"
            style={{ borderColor: "var(--border)" }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
